import React, { useEffect, useRef, useState } from "react";
import WordCloud from "wordcloud";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./presenter.css";
import CornerConfetti from "../../../components/cornerConfetti";
import { selectAdminToken } from "../../sessionSlice";
import { getRuntimeSession } from "../../../functions/getRuntimeSession";
import { useCountUp } from "../../../functions/useCountUp";
import { formatSec } from "../../../functions/scoring";
import {
  liveId,
  initLive,
  patchLive,
  startLive,
  beginCountdown,
  openQuestion,
  markTimesUp,
  pauseQuestion,
  resumeQuestion,
  subscribeLive,
  getLiveOnce,
  getParticipantCount,
  getActiveParticipantCount,
  subscribeTopParticipants,
  subscribeQuizResponses,
  subscribeFastestCorrect,
  subscribeVotes,
  subscribeWords,
  removeWord,
  subscribePins,
  subscribeFastestCorrectPins,
} from "../../../functions/liveSession";

const TYPE_LABEL = { quiz: "Quiz", poll: "Poll", wordcloud: "Word Cloud", pindrop: "Pin Drop" };

// Validate that every activity has complete content. Returns string[] of issues.
function validateSession(runtime) {
  const issues = [];
  const activities = runtime?.activities || [];
  if (activities.length === 0) {
    issues.push("This session has no activities. Add at least one before presenting.");
    return issues;
  }
  activities.forEach((a, i) => {
    const label = `Activity ${i + 1} (${TYPE_LABEL[a.type] || a.type})`;
    const qs = a.questions || [];
    if (qs.length === 0) {
      issues.push(`${label}: no questions/items added.`);
      return;
    }
    qs.forEach((q, qi) => {
      const n = `${label} · Q${qi + 1}`;
      if (a.type === "quiz") {
        if (!q.question_text || !q.question_text.trim()) issues.push(`${n}: missing question text.`);
        const opts = (q.options || []).filter((o) => o && o.trim());
        if (opts.length < 2) issues.push(`${n}: needs at least 2 options.`);
        if (!q.answer_index || q.answer_index < 1 || q.answer_index > (q.options || []).length)
          issues.push(`${n}: no correct answer selected.`);
      } else if (a.type === "poll") {
        if (!q.question_text || !q.question_text.trim()) issues.push(`${n}: missing question text.`);
        const opts = (q.options || []).filter((o) => o && o.trim());
        if (opts.length < 2) issues.push(`${n}: needs at least 2 options.`);
      } else if (a.type === "wordcloud") {
        if (!q.prompt || !q.prompt.trim()) issues.push(`${n}: missing prompt.`);
      } else if (a.type === "pindrop") {
        if (!q.image_url) issues.push(`${n}: no image uploaded.`);
        if (!q.polygons || q.polygons.length === 0) issues.push(`${n}: no drop zone drawn.`);
      }
    });
  });
  return issues;
}

function useCountdown(endsAt) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    // Refresh immediately when the deadline changes (not just on the next
    // 250ms tick) — otherwise the first render after a new endsAt arrives
    // can use a `now` that's up to 250ms stale, which rounds a clean 30.00s
    // timer up to displaying "31" via Math.ceil for that first render.
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [endsAt]);
  if (!endsAt) return null;
  const remaining = Math.max(0, Math.ceil((endsAt - now) / 1000));
  return remaining;
}

const Presenter = () => {
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const organizationId = useSelector((s) => s.session?.organizationId);
  const sessionId = useSelector((s) => s.session?.sessionId);
  const currentTheme = useSelector(
    (s) => s.theme?.currentTheme || s.theme?.data?.themename || null
  );

  const [runtime, setRuntime] = useState(null); // {settings, activities}
  const [live, setLive] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [totalCount, setTotalCount] = useState(0); // cumulative — everyone who has ever joined (Lobby's "N joined")
  const [liveCount, setLiveCount] = useState(0); // currently here — heartbeat within the last 45s (bottom-left badge)
  const [loading, setLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState([]);

  const id = currentTheme ? liveId(organizationId, sessionId, currentTheme) : null;
  const remaining = useCountdown(live?.questionEndsAt);
  const countdownLeft = useCountdown(live?.countdownEndsAt);

  // Load runtime config + init live doc + subscribe.
  useEffect(() => {
    if (!id || !currentTheme) return;
    let unsubLive = () => {};
    let unsubParts = () => {};
    (async () => {
      try {
        const data = await getRuntimeSession({ token: adminToken, currentTheme });
        setRuntime(data);

        // Guard: don't allow presenting an incomplete session.
        const issues = validateSession(data);
        if (issues.length > 0) {
          setValidationErrors(issues);
          return; // do not init live / subscribe
        }

        await initLive(id, { mode: data.settings.mode });
        unsubLive = subscribeLive(id, (d) => setLive(d));
        // Bounded to the top 15 by points — the presenter never needs the
        // full roster (that's what participants.jsx's one-time export is
        // for), and an unscoped listener here re-delivers every participant
        // doc on every single participant's write, which doesn't scale.
        unsubParts = subscribeTopParticipants(id, 15, (p) => setParticipants(p));
      } catch (e) {
        Swal.fire("Error", e.message || "Failed to open presenter", "error");
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      unsubLive();
      unsubParts();
    };
  }, [id, currentTheme, adminToken]);

  // Snapshot the running question's real end time to localStorage on every
  // change, scoped to this exact session+theme. This is ONLY an accuracy
  // upgrade for OUR OWN reconnect below — never read by anyone else, never
  // written to Firestore by anyone but the presenter.
  useEffect(() => {
    if (!id || !live) return;
    if (live.phase === "question" && live.questionEndsAt && !live.paused) {
      try {
        localStorage.setItem(`pp2_timer_${id}`, JSON.stringify({ questionEndsAt: live.questionEndsAt }));
      } catch (e) {
        // localStorage unavailable (private browsing / quota) — reconnect
        // just falls back to the generic resumeQuestion() below instead.
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, live?.phase, live?.questionEndsAt, live?.paused]);

  // Resume a paused question accurately when THIS presenter reconnects
  // (same browser, tab was closed/reloaded mid-question). Firestore's own
  // pausedRemaining is a relative "seconds left when I paused" snapshot, so
  // resuming from it after being gone a while hands back time that
  // shouldn't still be there. Our own localStorage snapshot has the real
  // absolute end time instead, so we restore from that when we have it and
  // fall back to the generic (less precise) resumeQuestion() otherwise —
  // e.g. a brand new browser/device with no local history for this session.
  // Gated on Firestore's OWN state (phase==="question" && paused===true),
  // not run blindly on mount, so a stale localStorage entry from an old/
  // reset session can never resurrect a question that's already moved on.
  const resumeAttempted = useRef(false);
  useEffect(() => {
    if (!id || !live) return;
    if (live.phase !== "question" || !live.paused) {
      resumeAttempted.current = false; // armed again for the NEXT pause
      return;
    }
    if (resumeAttempted.current) return;
    resumeAttempted.current = true;

    (async () => {
      let usedLocal = false;
      try {
        const raw = localStorage.getItem(`pp2_timer_${id}`);
        const snap = raw ? JSON.parse(raw) : null;
        if (snap && snap.questionEndsAt) {
          usedLocal = true;
          if (Date.now() >= snap.questionEndsAt) {
            await markTimesUp(id);
          } else {
            await patchLive(id, { phase: "question", questionEndsAt: snap.questionEndsAt, paused: false, pausedRemaining: null });
          }
        }
      } catch (e) {
        usedLocal = false;
      }
      if (!usedLocal) {
        await resumeQuestion(id).catch(() => {});
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, live?.phase, live?.paused]);

  // Pause on unmount (presenter leaving the screen) — freezes the timer for
  // fairness while unattended. The effects above handle resuming it
  // accurately once the presenter (this same browser) reconnects.
  useEffect(() => {
    if (!id) return undefined;
    return () => {
      pauseQuestion(id).catch(() => {});
    };
  }, [id]);

  // Two distinct numbers, both polled count aggregations — never a live
  // listener on the full participants collection, never a stored/
  // incremented counter (that approach drifted in practice: a participant
  // who joined before the control doc existed could lose their increment
  // and the number would silently undercount forever).
  //   totalCount — cumulative, everyone who has ever joined (Lobby's big
  //     "N joined" number — intentionally does NOT drop people who left).
  //   liveCount  — currently here, heartbeat within the last 45s (the
  //     bottom-left badge — this is the one that should reflect drops).
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    const refresh = () => {
      Promise.all([getParticipantCount(id), getActiveParticipantCount(id)])
        .then(([total, active]) => {
          if (!cancelled) {
            setTotalCount(total);
            setLiveCount(active);
          }
        })
        .catch(() => {});
    };
    refresh();
    const interval = setInterval(refresh, 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [id]);

  // Same class of problem as the participant side (see play.jsx): a
  // backgrounded tab (screen lock, app switch — most relevant if the
  // presenter is run from a phone/tablet) can leave the onSnapshot
  // listener's connection stale after the tab is foregrounded again. This
  // is worse here than on the participant side, since the presenter is the
  // one device whose writes drive every participant's phase transitions —
  // a stale `live` here can make the whole session look frozen to everyone,
  // not just this one screen. Force a fresh read on resume to self-heal.
  useEffect(() => {
    if (!id) return undefined;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      getLiveOnce(id)
        .then((d) => d && setLive(d))
        .catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [id]);

  // Countdown (3-2-1) finished -> open the question with its per-question timer.
  useEffect(() => {
    if (live?.phase !== "countdown" || countdownLeft == null) return;
    if (countdownLeft <= 0) {
      const act = runtime?.activities?.[live.currentIndex];
      const secs = act?.timer_seconds || 30;
      openQuestion(id, secs).catch(() => {});
    }
  }, [countdownLeft, live?.phase, live?.currentIndex, id, runtime]);

  // Question time elapsed -> Time's up.
  useEffect(() => {
    if (live?.phase === "question" && !live?.paused && remaining === 0 && live?.questionEndsAt) {
      markTimesUp(id).catch(() => {});
    }
  }, [remaining, live?.phase, live?.paused, live?.questionEndsAt, id]);

  if (loading) {
    return (
      <div className="pr-stage pr-loading">
        <div className="load-theme-spinner"></div>
        <p>Loading presenter…</p>
      </div>
    );
  }

  if (!runtime || !currentTheme) {
    return (
      <div className="pr-stage pr-loading">
        <p>No session selected.</p>
        <button className="pr-start-btn" onClick={() => navigate("/admin/session/activities")}>
          Back
        </button>
      </div>
    );
  }

  if (validationErrors.length > 0) {
    return (
      <div className="pr-stage">
        <div className="pr-header">
          <div className="pr-header-left">
            <button className="pr-exit" onClick={() => navigate("/admin/session/activities")} title="Back to activities">
              <i className="fa-solid fa-xmark"></i>
            </button>
            <span className="pr-activity-label">Cannot present yet</span>
          </div>
        </div>
        <div className="pr-center">
          <div className="pr-card pr-errors">
            <div className="pr-err-icon"><i className="fa-solid fa-triangle-exclamation"></i></div>
            <DecoHeading className="pr-q-text">Fix these before presenting</DecoHeading>
            <p className="pr-err-sub">
              Every activity must have complete content. Please resolve the following:
            </p>
            <ul className="pr-err-list">
              {validationErrors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
            <button className="pr-start-btn" onClick={() => navigate("/admin/session/activities")}>
              Go to Activities
            </button>
          </div>
        </div>
      </div>
    );
  }

  const activities = runtime.activities || [];
  const settings = runtime.settings;
  const activityCount = activities.length;
  const idx = live?.currentIndex ?? 0;
  const activity = activities[idx] || null;
  const questions = activity?.questions || [];
  const qCount = questions.length;
  const qIdx = live?.currentQuestionIndex ?? 0;
  const phase = live?.phase || "lobby";

  const handleStart = async () => {
    if (activityCount === 0) {
      Swal.fire("", "This session has no activities.", "warning");
      return;
    }
    await startLive(id); // -> countdown for first question
  };

  const goToQuestion = async (newIndex, newQIdx) => {
    // Always run the 3-2-1 countdown before a question appears.
    await beginCountdown(id, { currentIndex: newIndex, currentQuestionIndex: newQIdx });
  };

  // ▶ advance: countdown is auto; question/timesup -> reveal -> qleaderboard -> next q -> next activity -> final
  const advance = async () => {
    if (!activity) return;
    if (phase === "countdown") return; // let the countdown finish
    const isQuiz = activity.type === "quiz";
    const isPoll = activity.type === "poll";
    // Pin Drop is now scored the same way as Quiz (per-zone correctness),
    // so it gets the same reveal -> leaderboard flow.
    const isScoredPindrop = activity.type === "pindrop";

    // From an open question or time's up, quiz/pindrop reveal first (poll only if presenter-reveal).
    if (phase === "question" || phase === "timesup") {
      if (isQuiz || isScoredPindrop) return patchLive(id, { phase: "reveal", questionEndsAt: null });
      if (isPoll && settings.poll_display_mode === "presenter_reveal")
        return patchLive(id, { phase: "reveal", pollReveal: true, questionEndsAt: null });
      // wordcloud / live-poll: fall through to next question
    }
    if ((isQuiz || isScoredPindrop) && phase === "reveal") return patchLive(id, { phase: "qleaderboard" });

    // Next question within the activity
    if (qIdx + 1 < qCount) return goToQuestion(idx, qIdx + 1);
    // Next activity
    if (idx + 1 < activityCount) return goToQuestion(idx + 1, 0);
    // End
    return patchLive(id, { phase: "finalleaderboard", status: "ended", questionEndsAt: null });
  };

  // ◀ read-only back: show the previous question/activity's content directly.
  // No countdown, no new timer — matches "revisiting doesn't reopen submissions".
  const goBack = async () => {
    if (phase === "finalleaderboard") {
      return patchLive(id, { phase: "reveal", status: "running", questionEndsAt: null });
    }
    if (qIdx > 0) {
      return patchLive(id, {
        currentQuestionIndex: qIdx - 1,
        phase: "reveal",
        pollReveal: true,
        questionEndsAt: null,
        countdownEndsAt: null,
      });
    }
    if (idx > 0) {
      const prevActivity = activities[idx - 1];
      const prevQCount = (prevActivity?.questions || []).length;
      return patchLive(id, {
        currentIndex: idx - 1,
        currentQuestionIndex: Math.max(0, prevQCount - 1),
        phase: "reveal",
        pollReveal: true,
        questionEndsAt: null,
        countdownEndsAt: null,
      });
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const headerLabel =
    phase === "finalleaderboard"
      ? "Final Leaderboard"
      : activity
      ? `${TYPE_LABEL[activity.type]} · Question ${qIdx + 1} of ${qCount}`
      : "";

  // Desktop background from the theme.
  const bgUrl = settings.background_desk
    ? `${process.env.REACT_APP_S3_PATH}${settings.background_desk}`
    : null;
  const stageStyle = bgUrl
    ? { backgroundImage: `url(${bgUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
    : {};

  const inQuestion = phase === "question" || phase === "timesup";

  return (
    <div className="pr-stage pr-stage-bg" style={stageStyle}>
      {/* Header: NO session timer. Just activity label + exit. */}
      <div className="pr-header">
        <div className="pr-header-left">
          <button className="pr-exit" onClick={() => navigate("/admin/session/activities")} title="Exit presenter">
            <i className="fa-solid fa-xmark"></i>
          </button>
          <span className="pr-activity-label">{headerLabel}</span>
          {activityCount > 0 && phase !== "finalleaderboard" && (
            <span className="pr-activity-pos">
              Activity {idx + 1} / {activityCount}
            </span>
          )}
        </div>
      </div>

      {/* Main row: content + side arrow rail (no page scroll) */}
      <div className="pr-main-row">
        <div className="pr-center">
          {phase === "lobby" || live?.status === "waiting" ? (
            <Lobby
              title={settings.title || runtime.themename}
              description={settings.description}
              participants={participants}
              totalCount={totalCount}
              onStart={handleStart}
              canStart={activityCount > 0}
            />
          ) : phase === "countdown" ? (
            <CountdownOverlay left={countdownLeft} />
          ) : phase === "finalleaderboard" ? (
            <FinalLeaderboard id={id} />
          ) : activity ? (
            <div className="pr-q-wrap">
              {/* Per-question timer ABOVE the question */}
              {inQuestion && (
                <div className={`pr-qtimer ${phase === "timesup" ? "pr-qtimer-up" : ""}`}>
                  {phase === "timesup" ? (
                    <span className="pr-timesup">⏱ Time's up</span>
                  ) : (
                    <>
                      <i className="fa-regular fa-clock"></i>
                      <span className="pr-qtimer-num">{remaining == null ? "--" : remaining}s</span>
                    </>
                  )}
                </div>
              )}
              <PhaseCard
                id={id}
                activity={activity}
                qIdx={qIdx}
                phase={phase === "timesup" ? "question" : phase}
                pollReveal={live?.pollReveal}
                settings={settings}
              />
            </div>
          ) : (
            <div className="pr-card">No activity.</div>
          )}
        </div>

        {/* Side arrow rail (small, right side, vertically centered) */}
        {live?.status === "running" && (
          <div className="pr-side-nav">
            <button className="pr-arrow-sm" onClick={goBack} title="Previous">
              <i className="fa-solid fa-chevron-up"></i>
            </button>
            <button className="pr-arrow-sm pr-arrow-sm-next" onClick={advance} title="Next">
              <i className="fa-solid fa-chevron-down"></i>
            </button>
          </div>
        )}
      </div>

      {/* Bottom-left: user count + fullscreen */}
      <div className="pr-bottom-left">
        <span className="pr-usercount">
          <i className="fa-solid fa-user"></i> {liveCount}
        </span>
        <button className="pr-fs-btn" onClick={toggleFullscreen} title="Fullscreen">
          <i className="fa-solid fa-expand"></i>
        </button>
      </div>
    </div>
  );
};

// ---- 3-2-1 countdown overlay -------------------------------------------
function CountdownOverlay({ left }) {
  const n = left == null ? 3 : Math.max(1, left);
  return (
    <div className="pr-countdown">
      <div className={`pr-countdown-num pr-countdown-num-${n}`} key={n}>
        {n}
        <span className="pr-count-burst">
          {Array.from({ length: 8 }).map((_, i) => (
            <span key={i} className="pr-count-particle" style={{ "--i": i }} />
          ))}
        </span>
      </div>
      <div className="pr-countdown-label">Get ready…</div>
    </div>
  );
}

// ---- "Top Responses" fastest-correct leaderboard (quiz + pin drop) -----
const STOPWATCH_COLORS = ["#e0931a", "#8b5cf6", "#2f6bff", "#17b8c4", "#19a463"];

// Generic decorated headline — small colored triangle flourishes either
// side. Used on every result/status headline (reveal, leaderboards, lobby
// title, errors) but deliberately NOT on the live "question" text itself
// (quiz question, poll prompt, wordcloud prompt, pindrop's live prompt) —
// that's the one moment that should stay plain and undistracted.
function DecoHeading({ as: Tag = "h1", className = "", children }) {
  return (
    <Tag className={`pr-deco-title ${className}`}>
      <span className="pr-deco-tri pr-deco-tri-l">
        <i className="fa-solid fa-play" style={{ color: "#f2a93b" }}></i>
        <i className="fa-solid fa-play" style={{ color: "#8b5cf6" }}></i>
      </span>
      {children}
      <span className="pr-deco-tri pr-deco-tri-r">
        <i className="fa-solid fa-play" style={{ color: "#d23b78" }}></i>
        <i className="fa-solid fa-play" style={{ color: "#f2a93b" }}></i>
      </span>
    </Tag>
  );
}

// ---- Color palette for bars — curated gradients, not tied to correctness
// (poll never has a "correct" option). The correct quiz option instead gets
// its own checkmark badge, so the palette stays consistent either way.
const BAR_PALETTE = [
  "linear-gradient(90deg, #5b2fe0 0%, #c84fd6 100%)", // purple -> pink
  "linear-gradient(90deg, #19c48f 0%, #39d9ab 100%)", // teal
  "linear-gradient(90deg, #f2a93b 0%, #ffcf6b 100%)", // amber
  "linear-gradient(90deg, #ef5b5b 0%, #ff8a8a 100%)", // coral
  "linear-gradient(90deg, #2f6bff 0%, #6a4bff 100%)", // blue -> indigo
  "linear-gradient(90deg, #17b8c4 0%, #37dbe6 100%)", // cyan
];

// ---- HorizontalBars: label-left / bar-middle / percent-right -----------
// Used by both Quiz (question + reveal) and Poll, so they look identical.
// `hidden` = counts/percentages are not shown yet (presenter-reveal mode).
function HorizontalBars({ options, counts, total, correctIndex, hidden, footer }) {
  // More than 4 options no longer fits comfortably at full size without
  // scrolling — shrink rows down so everything's visible at once instead.
  const compact = options.length > 4;
  return (
    <div className={`pr-hbars ${compact ? "pr-hbars-compact" : ""}`}>
      {options.map((label, i) => {
        const pct = total ? Math.round((counts[i] / total) * 100) : 0;
        const isCorrect = correctIndex != null && i === correctIndex;
        return (
          <HorizontalBar
            key={i}
            label={label}
            pct={hidden ? 0 : pct}
            index={i}
            color={BAR_PALETTE[i % BAR_PALETTE.length]}
            correct={isCorrect}
            hidden={hidden}
            compact={compact}
          />
        );
      })}
      {footer}
    </div>
  );
}

function HorizontalBar({ label, pct, index, color, correct, hidden, compact }) {
  const animated = useCountUp(pct, { duration: 800, delay: index * 110 });
  const rounded = Math.round(animated);
  return (
    <div
      className={`pr-hbar-row pr-hbar-animate ${compact ? "pr-hbar-row-compact" : ""} ${correct ? "pr-hbar-correct" : ""}`}
      style={{ animationDelay: `${index * 110}ms` }}
    >
      <div className="pr-hbar-label">
        {label}
        {correct && <i className="fa-solid fa-circle-check pr-hbar-check"></i>}
      </div>
      <div className="pr-hbar-track">
        <div className="pr-hbar-fill" style={{ width: `${animated}%`, background: color }} />
      </div>
      <div className="pr-hbar-value">
        {hidden ? <i className="fa-solid fa-lock"></i> : `${rounded}%`}
      </div>
    </div>
  );
}

// ---- Lobby with joining animation --------------------------------------
// Deterministic pastel-ish color per participant (stable across renders).
const AVATAR_PALETTE = ["#2f6bff", "#19a463", "#e0931a", "#d23b78", "#8b5cf6", "#17b8c4", "#e0433b"];
function avatarColor(seed) {
  let h = 0;
  const s = String(seed || "");
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}
function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/);
  const first = parts[0]?.[0] || "?";
  const second = parts[1]?.[0] || "";
  return (first + second).toUpperCase();
}

function Lobby({ title, description, participants, totalCount, onStart, canStart }) {
  // Cap the visual grid so a big crowd doesn't overwhelm the lobby card —
  // the exact count is still shown via the big number below. `participants`
  // is only a bounded sample (see subscribeTopParticipants) so overflow and
  // the big number are computed from the real `totalCount`, not its length.
  const MAX_AVATARS = 40;
  const shown = participants.slice(0, MAX_AVATARS);
  const overflow = totalCount - shown.length;

  return (
    <div className="pr-card pr-lobby">
      <div className="pr-pill">WAITING TO START</div>
      <DecoHeading className="pr-lobby-title">{title}</DecoHeading>
      {description ? <p className="pr-lobby-desc">{description}</p> : null}

      {/* Visual representation of people joining */}
      <div className="pr-avatar-grid">
        {shown.length === 0 && (
          <span className="pr-avatar-empty">Waiting for participants to join…</span>
        )}
        {shown.map((p, i) => (
          <span
            key={p.userId}
            className="pr-avatar-bubble"
            title={p.name || p.userId}
            style={{ background: avatarColor(p.userId), animationDelay: `${Math.min(i, 20) * 45}ms` }}
          >
            {initialsOf(p.name)}
          </span>
        ))}
        {overflow > 0 && <span className="pr-avatar-more">+{overflow}</span>}
      </div>

      <div className="pr-join-big">
        <span className="pr-join-number" key={totalCount}>{totalCount}</span>
        <span className="pr-join-word">joined</span>
      </div>

      <button className="pr-start-btn" onClick={onStart} disabled={!canStart}>
        Start Session
      </button>
    </div>
  );
}

// ---- Phase card: delegates per activity type ---------------------------
function PhaseCard({ id, activity, qIdx, phase, pollReveal, settings }) {
  if (activity.type === "quiz") {
    return <QuizPhase id={id} activity={activity} qIdx={qIdx} phase={phase} settings={settings} />;
  }
  if (activity.type === "poll") {
    return <PollPhase id={id} activity={activity} qIdx={qIdx} phase={phase} settings={settings} pollReveal={pollReveal} />;
  }
  if (activity.type === "wordcloud") {
    return <WordcloudPhase id={id} activity={activity} qIdx={qIdx} />;
  }
  if (activity.type === "pindrop") {
    return <PindropPhase id={id} activity={activity} qIdx={qIdx} phase={phase} />;
  }
  return <div className="pr-card">Unknown activity.</div>;
}

// ---- Quiz presenter phases ---------------------------------------------
function QuizPhase({ id, activity, qIdx, phase, settings }) {
  const q = activity.questions[qIdx];
  // Full per-question response set — still needed for the bar chart's
  // per-option counts/percentages (there's no way around seeing every
  // response to compute "how many chose each option" without it) and the
  // reveal-confetti "did anyone get it right" check.
  const [responses, setResponses] = useState([]);
  // Separate, bounded subscription for "who answered fastest" — a real
  // Firestore query (where correct==true, orderBy timeMs, limit 5), never
  // more than 5 docs over the wire regardless of how many thousand people
  // answered. Used for both the reveal "Fastest:" line and the dedicated
  // qleaderboard screen below — no client-side sort of the full set.
  const [fastestCorrect, setFastestCorrect] = useState([]);
  const [showConfetti, setShowConfetti] = useState(false);
  useEffect(() => {
    const unsub = subscribeQuizResponses(id, activity.id, qIdx, setResponses);
    return () => unsub();
  }, [id, activity.id, qIdx]);

  useEffect(() => {
    const unsub = subscribeFastestCorrect(id, activity.id, qIdx, 5, setFastestCorrect);
    return () => unsub();
  }, [id, activity.id, qIdx]);

  // Fire confetti once when entering reveal, if anyone answered correctly.
  useEffect(() => {
    if (phase !== "reveal") {
      setShowConfetti(false);
      return;
    }
    const anyCorrect = responses.some((r) => r.correct);
    if (anyCorrect) setShowConfetti(true);
  }, [phase, qIdx, activity.id, responses]);

  if (!q) return <div className="pr-card">No question.</div>;

  const total = responses.length || 0;
  const counts = q.options.map((_, i) => responses.filter((r) => r.optionIndex === i).length);
  const isLiveMode = (settings?.quiz_display_mode || "live") === "live";

  if (phase === "question") {
    return (
      <div className={`pr-card pr-card-wide ${hasMedia(q) ? "pr-has-media" : ""}`}>
        <MediaSplit q={q}>
          <div className="pr-pill">{q.format === "truefalse" ? "TRUE / FALSE" : q.format === "mythfact" ? "MYTH / FACT" : "MULTIPLE CHOICE"}</div>
          <DecoHeading className="pr-q-text">{q.question_text}</DecoHeading>
          <HorizontalBars
            options={q.options}
            counts={counts}
            total={total}
            hidden={!isLiveMode}
            footer={<div className="pr-submit-count" key={total}>{total} answered</div>}
          />
        </MediaSplit>
      </div>
    );
  }

  if (phase === "reveal") {
    return (
      <div className="pr-card pr-card-wide">
        {showConfetti && <CornerConfetti onDone={() => setShowConfetti(false)} />}
        <div className="pr-pill">CORRECT ANSWER</div>
        <DecoHeading className="pr-q-text">{q.options[q.answer_index - 1]}</DecoHeading>
        <HorizontalBars
          options={q.options}
          counts={counts}
          total={total}
          correctIndex={q.answer_index - 1}
        />
        {fastestCorrect[0] && (
          <div className="pr-fastest pr-fastest-in">
            <i className="fa-solid fa-crown"></i> Fastest: {fastestCorrect[0].name || fastestCorrect[0].userId} ·{" "}
            <span className="pr-fastest-pts">{fastestCorrect[0].points || 0} pts</span> ·{" "}
            {formatSec(fastestCorrect[0].timeMs)} sec
          </div>
        )}
      </div>
    );
  }

  // qleaderboard: top fastest correct (already bounded server-side — see
  // subscribeFastestCorrect above)
  if (phase === "qleaderboard") {
    const top = fastestCorrect;
    return (
      <div className="pr-card">
        <div className="pr-pill">FASTEST CORRECT ANSWERS</div>
        <DecoHeading className="pr-q-text">Top 5 Responses</DecoHeading>
        <div className="pr-lb-list">
          {top.length === 0 && <div className="pr-lb-empty">No correct answers.</div>}
          {top.map((r, i) => (
            <div
              className={`pr-lb-row pr-lb-animate ${i === 0 ? "pr-lb-first" : ""}`}
              style={{ animationDelay: `${i * 150}ms` }}
              key={r.userId}
            >
              {i === 0 && <Confetti />}
              <span className="pr-lb-rank">{i + 1}</span>
              <span className="pr-lb-name">
                {r.name || r.userId || "Participant"}
                <small>Correct</small>
              </span>
              <span className="pr-lb-points">{r.points || 0} pts</span>
              <span className="pr-lb-time">
                <span className="pr-lb-stopwatch" style={{ background: STOPWATCH_COLORS[i % STOPWATCH_COLORS.length] }}>
                  <i className="fa-solid fa-stopwatch"></i>
                </span>
                {formatSec(r.timeMs)} sec
              </span>
              {i === 0 && <span className="pr-lb-trophy">🏆</span>}
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
}

// ---- Word cloud (wordcloud2.js: canvas-packed tag cloud) ----------------
const WC_PALETTE = ["#2f6bff", "#19a463", "#e0931a", "#d23b78", "#8b5cf6", "#17b8c4", "#e0433b"];
const WC_FONT = '"FiraSans-Medium", "Segoe UI", Arial, sans-serif';
const WC_MAX_WORDS = 100;

function WordCloudCanvas({ entries }) {
  const boxRef = useRef(null);
  const canvasRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  // Track the container size so the canvas always matches what's on screen.
  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize((prev) =>
        Math.abs(prev.w - r.width) > 1 || Math.abs(prev.h - r.height) > 1
          ? { w: Math.round(r.width), h: Math.round(r.height) }
          : prev
      );
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // Top N by frequency — signature so we only redraw (CPU-heavy) when
  // words/counts/size actually change, not on every unrelated re-render.
  const top = [...entries].sort((a, b) => b[1].count - a[1].count).slice(0, WC_MAX_WORDS);
  const signature = `${size.w}x${size.h}|` + top.map(([w, i]) => `${w}:${i.count}`).join(",");

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !size.w || !size.h || top.length === 0) return undefined;
    let cancelled = false;

    const counts = top.map(([, i]) => i.count);
    const maxC = Math.max(...counts);
    const minC = Math.min(...counts);
    const minFont = Math.max(14, Math.round(size.h * 0.05));
    const maxFont = Math.max(minFont + 8, Math.min(Math.round(size.h * 0.26), 96));
    // sqrt scale: keeps big words big without starving the small ones.
    const fontFor = (c) => {
      if (maxC === minC) return Math.round((minFont + maxFont) / 2);
      const t = (Math.sqrt(c) - Math.sqrt(minC)) / (Math.sqrt(maxC) - Math.sqrt(minC));
      return Math.round(minFont + t * (maxFont - minFont));
    };

    // Grid size (word spacing) scales with canvas width and shrinks as more
    // distinct words need to fit.
    const gridSize = Math.max(4, Math.round(size.w / 90) - Math.round(top.length / 14));

    const draw = () => {
      if (cancelled) return;
      // wordcloud2 lays words out directly in canvas.width/height device
      // pixels — it has no DPR concept of its own. So the buffer is sized
      // up for sharpness, but every measurement handed to it (font sizes,
      // gridSize, minSize) must be scaled up by the same factor too. Doing
      // the sizing here AND pre-scaling the context (as before) double-
      // counted the DPR factor, which is what pushed words outside the
      // visible card on retina displays.
      const dpr = window.devicePixelRatio || 1;
      canvas.width = size.w * dpr;
      canvas.height = size.h * dpr;
      canvas.style.width = `${size.w}px`;
      canvas.style.height = `${size.h}px`;

      WordCloud(canvas, {
        list: top.map(([text, info]) => [text, fontFor(info.count) * dpr]),
        weightFactor: (w) => w,
        fontFamily: WC_FONT,
        fontWeight: 800,
        gridSize: gridSize * dpr,
        minSize: 10 * dpr,
        backgroundColor: "transparent",
        color: () => WC_PALETTE[Math.floor(Math.random() * WC_PALETTE.length)],
        rotateRatio: 0.3,
        rotationSteps: 2,
        shrinkToFit: true,
        drawOutOfBound: false,
        clearCanvas: true,
      });
    };

    // Wait for webfonts so canvas measurement matches the font actually drawn.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(draw).catch(draw);
    } else {
      draw();
    }

    return () => {
      cancelled = true;
      WordCloud.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return (
    <div className="pr-wordcloud-canvas" ref={boxRef}>
      {entries.length === 0 && <span className="pr-wc-empty pr-wc-empty-abs">Waiting for responses…</span>}
      {size.w > 0 && top.length > 0 && <canvas ref={canvasRef} className="pr-wc-canvas" />}
    </div>
  );
}

// Subtle one-shot sparkle burst for the #1 row.
function Confetti() {
  const bits = Array.from({ length: 10 });
  return (
    <div className="pr-confetti">
      {bits.map((_, i) => (
        <span key={i} className={`pr-confetti-bit pr-cb-${i % 5}`} style={{ animationDelay: `${i * 40}ms` }} />
      ))}
    </div>
  );
}

function QuestionMedia({ q }) {
  if (!q.media_url || q.media_type === "none") return null;
  const src = process.env.REACT_APP_S3_PATH + q.media_url;
  if (q.media_type === "image") return <img className="pr-media" src={src} alt="" />;
  if (q.media_type === "video") return <video className="pr-media" src={src} controls />;
  if (q.media_type === "audio") return <audio className="pr-media" src={src} controls />;
  return null;
}

function hasMedia(q) {
  return Boolean(q?.media_url) && q.media_type !== "none";
}

// On desktop, media sits to the left and the question/results sit to the
// right so a wide card with an image/video + several options doesn't scroll.
function MediaSplit({ q, children }) {
  if (!hasMedia(q)) return <>{children}</>;
  return (
    <div className="pr-media-layout">
      <div className="pr-media-pane">
        <QuestionMedia q={q} />
      </div>
      <div className="pr-content-pane">{children}</div>
    </div>
  );
}

// ---- Poll presenter phase ----------------------------------------------
function PollPhase({ id, activity, qIdx, phase, settings, pollReveal }) {
  const q = activity.questions[qIdx];
  const [votes, setVotes] = useState([]);
  useEffect(() => {
    const unsub = subscribeVotes(id, activity.id, qIdx, setVotes);
    return () => unsub();
  }, [id, activity.id, qIdx]);

  if (!q) return <div className="pr-card">No question.</div>;
  const total = votes.length;
  const counts = q.options.map((_, i) => votes.filter((v) => v.optionIndex === i).length);
  const showResults = settings.poll_display_mode === "live" || phase === "reveal" || pollReveal;

  return (
    <div className={`pr-card pr-card-wide ${hasMedia(q) ? "pr-has-media" : ""}`}>
      <MediaSplit q={q}>
        <div className="pr-pill">LIVE POLL</div>
        <DecoHeading className="pr-q-text">{q.question_text}</DecoHeading>
        <HorizontalBars
          options={q.options}
          counts={counts}
          total={total}
          hidden={!showResults}
          footer={
            showResults ? (
              <div className="pr-submit-count" key={total}>{total} votes</div>
            ) : (
              <div className="pr-poll-hidden pr-poll-hidden-inline">
                <i className="fa-solid fa-eye-slash"></i>
                <p>{total} submitted — results hidden until revealed (press ▶).</p>
              </div>
            )
          }
        />
      </MediaSplit>
    </div>
  );
}

// ---- Word cloud presenter phase ----------------------------------------
function WordcloudPhase({ id, activity, qIdx }) {
  const q = activity.questions[qIdx];
  const [words, setWords] = useState([]);
  useEffect(() => {
    const unsub = subscribeWords(id, activity.id, qIdx, setWords);
    return () => unsub();
  }, [id, activity.id, qIdx]);

  if (!q) return <div className="pr-card">No prompt.</div>;

  // Frequency map, case-insensitively merged (exclude removed) — otherwise
  // "Apple" / "apple" / "APPLE" count as three separate 1-count words
  // instead of one properly-weighted word, which flattens the whole cloud.
  // The displayed label keeps whichever casing variant was submitted most.
  const freq = {};
  words.filter((w) => !w.removed).forEach((w) => {
    const raw = (w.text || "").trim();
    if (!raw) return;
    const key = raw.toLowerCase();
    if (!freq[key]) freq[key] = { count: 0, docId: w.docId, labelCounts: {} };
    freq[key].count += 1;
    freq[key].docId = w.docId;
    freq[key].labelCounts[raw] = (freq[key].labelCounts[raw] || 0) + 1;
  });
  const entries = Object.entries(freq)
    .map(([key, info]) => {
      const label = Object.entries(info.labelCounts).sort((a, b) => b[1] - a[1])[0][0];
      return [label, { count: info.count, docId: info.docId }];
    })
    .sort((a, b) => b[1].count - a[1].count);
  const activeCount = words.filter((w) => !w.removed).length;

  const handleRemove = async () => {
    const latest = words.filter((w) => !w.removed).slice(-1)[0];
    if (latest) await removeWord(id, latest.docId);
  };

  return (
    <div className="pr-card pr-card-wide">
      <div className="pr-pill">LIVE RESPONSES</div>
      <DecoHeading className="pr-q-text">{q.prompt}</DecoHeading>
      <WordCloudCanvas entries={entries} />
      <div className="pr-wc-footer">
        <button className="pr-wc-remove" onClick={handleRemove}>Remove latest response</button>
        <span className="pr-wc-badge">Profanity filter active</span>
      </div>
      <div className="pr-wc-meta">
        <span>{activeCount} responses</span>
        <span>No scoring</span>
      </div>
    </div>
  );
}

// Standard ray-casting point-in-polygon test, used to score pins and compute
// per-zone correct percentages on the presenter's reveal screen.
function pointInPolygonPresenter(px, py, points) {
  let inside = false;
  for (let i = 0, j = points.length - 1; i < points.length; j = i, i += 1) {
    const xi = points[i].x, yi = points[i].y;
    const xj = points[j].x, yj = points[j].y;
    const intersects =
      (yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

function polygonToPathPresenter(points, W, H) {
  return points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x * W} ${p.y * H}`).join(" ") + " Z";
}

// ---- Pin drop presenter phase ------------------------------------------
function PindropPhase({ id, activity, qIdx, phase }) {
  const q = activity.questions[qIdx];
  // Full set — still needed for the per-zone landing percentages (visual
  // heatmap) and the overall correct-count footer below.
  const [pins, setPins] = useState([]);
  // Bounded, server-sorted top-5-fastest-correct — see QuizPhase above for
  // the same reasoning.
  const [fastestCorrectPins, setFastestCorrectPins] = useState([]);
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    const unsub = subscribePins(id, activity.id, qIdx, setPins);
    return () => unsub();
  }, [id, activity.id, qIdx]);

  useEffect(() => {
    const unsub = subscribeFastestCorrectPins(id, activity.id, qIdx, 5, setFastestCorrectPins);
    return () => unsub();
  }, [id, activity.id, qIdx]);

  if (!q) return <div className="pr-card">No pin drop.</div>;
  const pct = (v) => `${v * 100}%`;
  const polygons = q.polygons || [];
  const total = pins.length;
  const correctCount = pins.filter((p) => p.correct).length;

  // Per-zone correct % — counts pins landing inside each polygon.
  const zoneStats = polygons.map((poly) => {
    const inZone = pins.filter((p) => pointInPolygonPresenter(p.x, p.y, poly.points));
    return { count: inZone.length, pct: total ? Math.round((inZone.length / total) * 100) : 0 };
  });

  const isReveal = phase === "reveal";

  if (phase === "qleaderboard") {
    const top = fastestCorrectPins;
    return (
      <div className="pr-card">
        <div className="pr-pill">FASTEST CORRECT PINS</div>
        <DecoHeading className="pr-q-text">Top 5 Responses</DecoHeading>
        <div className="pr-lb-list">
          {top.length === 0 && <div className="pr-lb-empty">No correct pins.</div>}
          {top.map((p, i) => (
            <div
              className={`pr-lb-row pr-lb-animate ${i === 0 ? "pr-lb-first" : ""}`}
              style={{ animationDelay: `${i * 150}ms` }}
              key={p.docId}
            >
              {i === 0 && <Confetti />}
              <span className="pr-lb-rank">{i + 1}</span>
              <span className="pr-lb-name">
                {p.name}
                <small>Correct</small>
              </span>
              <span className="pr-lb-points">{p.points || 0} pts</span>
              <span className="pr-lb-time">
                <span className="pr-lb-stopwatch" style={{ background: STOPWATCH_COLORS[i % STOPWATCH_COLORS.length] }}>
                  <i className="fa-solid fa-stopwatch"></i>
                </span>
                {formatSec(p.timeMs)} sec
              </span>
              {i === 0 && <span className="pr-lb-trophy">🏆</span>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const STAGE_W = 1000;
  const STAGE_H = 1000;
  const centroid = (points) => ({
    x: points.reduce((s, p) => s + p.x, 0) / points.length,
    y: points.reduce((s, p) => s + p.y, 0) / points.length,
  });

  return (
    <div className="pr-card">
      <div className="pr-pill">{isReveal ? "RESULTS" : "LIVE PIN ACTIVITY"}</div>
      <DecoHeading className="pr-q-text">{q.prompt}</DecoHeading>
      <div className="pr-pin-stage-wrap">
        <div className="pr-pin-stage" style={{ transform: `scale(${zoom})` }}>
          {q.image_url && <img src={process.env.REACT_APP_S3_PATH + q.image_url} alt="" draggable={false} />}
          {/* Correct zones are revealed (green) only after the answer reveal —
              during the live question nothing hints at where to pin. */}
          {isReveal && (
            <svg className="pr-pin-svg" viewBox={`0 0 ${STAGE_W} ${STAGE_H}`} preserveAspectRatio="none">
              {polygons.map((poly, i) => (
                <path
                  key={i}
                  d={polygonToPathPresenter(poly.points, STAGE_W, STAGE_H)}
                  className="pr-pin-zone-path"
                />
              ))}
            </svg>
          )}
          {isReveal &&
            polygons.map((poly, i) => {
              const c = centroid(poly.points);
              // Keep the badge fully inside the image: clamp the anchor away
              // from the edges, and counter-scale so zoom doesn't enlarge it.
              const cx = Math.min(0.9, Math.max(0.1, c.x));
              const cy = Math.min(0.88, Math.max(0.12, c.y));
              return (
                <span
                  key={`pct${i}`}
                  className="pr-pin-zone-pct"
                  style={{
                    left: pct(cx),
                    top: pct(cy),
                    transform: `translate(-50%, -50%) scale(${1 / zoom})`,
                  }}
                >
                  {zoneStats[i].pct}%
                </span>
              );
            })}
          {pins.map((p, i) => (
            <div
              key={p.docId}
              className={`pr-pin pr-pin-pop ${isReveal ? (p.correct ? "pr-pin-correct" : "pr-pin-wrong") : ""}`}
              style={{ left: pct(p.x), top: pct(p.y), animationDelay: `${i * 40}ms` }}
            >
              {zoom > 1.1 && <span className="pr-pin-tag">{p.name}</span>}
              <i className="fa-solid fa-location-dot"></i>
            </div>
          ))}
        </div>
      </div>
      <div className="pr-zoom">
        <button onClick={() => setZoom((z) => Math.max(1, z - 0.25))}>−</button>
        <span>{Math.round(zoom * 100)}% zoom</span>
        <button onClick={() => setZoom((z) => Math.min(3, z + 0.25))}>+</button>
      </div>
      {isReveal && (
        <div className="pr-submit-count" key={`${correctCount}-${total}`}>
          {correctCount} of {total} correct
        </div>
      )}
    </div>
  );
}

// ---- Final leaderboard -------------------------------------------------
// Same plain .pr-card/.pr-lb-row look as every other leaderboard — just
// colored borders on the top 3 (gold/silver/bronze) to mark the winners.
// Self-managed subscription — a direct Firestore query (orderBy points
// desc, limit 10), never a client-side filter/sort over every participant.
function FinalLeaderboard({ id }) {
  const [ranked, setRanked] = useState([]);
  useEffect(() => {
    const unsub = subscribeTopParticipants(id, 10, setRanked);
    return () => unsub();
  }, [id]);
  return (
    <div className="pr-card">
      <div className="pr-pill">FINAL LEADERBOARD</div>
      <DecoHeading className="pr-q-text">Final Results</DecoHeading>
      <div className="pr-lb-list pr-lb-list-compact">
        {ranked.length === 0 && <div className="pr-lb-empty">No participants.</div>}
        {ranked.map((p, i) => (
          <div
            className={`pr-lb-row pr-lb-row-compact pr-lb-animate pr-lb-podium-${i + 1} ${i === 0 ? "pr-lb-first" : ""}`}
            style={{ animationDelay: `${i * 150}ms` }}
            key={p.userId}
          >
            {i === 0 && <Confetti />}
            <span className="pr-lb-rank">{i + 1}</span>
            <span className="pr-lb-name">{p.name || p.userId || "Participant"}</span>
            <span className="pr-lb-time">
              {p.points || 0} pts · {p.correctCount || 0} correct
            </span>
            {i === 0 && <span className="pr-lb-trophy">🏆</span>}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Presenter;
